import { renderFields } from "@highnoteplatform/card-viewer";
import type {
  CardFieldSelectors,
  CardIssuerClient,
  CardViewerField,
  RenderCardTokenOptions,
} from "utils/cards/cardViewer";

type FieldStyles = Parameters<
  typeof renderFields
>[0]["elements"]["cardNumber"]["styles"];

export const highnoteCardClient: CardIssuerClient = {
  features: { copyButton: false },
  render: renderHighnoteCard,
};

async function renderHighnoteCard({
  cardToken: { issuerId, secret },
  selectors,
  style,
  onCopy,
  onLateError,
  isCurrent = () => true,
}: RenderCardTokenOptions): Promise<boolean> {
  // Only the six declarations Highnote allows; `fontSmoothing` is Stripe's.
  const { fontSmoothing, iconColor, ...base } = style.base ?? {};
  const fieldStyles = Object.fromEntries(
    Object.entries(base).filter(([, value]) => Boolean(value)),
  );

  await renderHighnoteCardFields({
    clientToken: secret,
    paymentCardId: issuerId,
    selectors: {
      cardNumber: selectors.cardNumber,
      cvv: selectors.cvv,
      expirationDate: selectors.expirationDate,
    },
    styles: fieldStyles,
    onCopy,
    onLateError,
  });
  return isCurrent();
}

// Chrome can keep showing a Highnote frame's blank first paint until the frame's size changes.
const repaintFrames = (selectors: CardFieldSelectors) => {
  const frames = Object.values(selectors).flatMap((selector) =>
    Array.from(
      document.querySelectorAll<HTMLIFrameElement>(`${selector} > iframe`),
    ),
  );
  frames.forEach((frame) => {
    frame.style.width = `${frame.offsetWidth + 1}px`;
  });
  requestAnimationFrame(() =>
    requestAnimationFrame(() =>
      frames.forEach((frame) => frame.style.removeProperty("width")),
    ),
  );
};

/** Resolves once the fields have loaded and rejects when the SDK reports a failure first. */
const renderHighnoteCardFields = ({
  clientToken,
  paymentCardId,
  selectors,
  styles,
  onCopy,
  onLateError,
}: {
  clientToken: string;
  paymentCardId: string;
  selectors: CardFieldSelectors;
  styles: FieldStyles;
  onCopy: (field: CardViewerField) => void;
  /** A failure the viewer reports after the fields loaded. */
  onLateError: (error: unknown) => void;
}) =>
  new Promise<void>((resolve, reject) => {
    let settled = false;
    let ready = false;
    let toggleMask: (() => void) | undefined;
    // `onReady` and the render promise can arrive in either order; unmask once both have.
    const unmask = () => {
      if (!ready || !toggleMask) return;
      toggleMask();
      repaintFrames(selectors);
    };

    renderFields({
      clientToken,
      paymentCardId,
      enableClipboard: true,
      ...(process.env.REACT_APP_SENTRY_ENV === "production" && {
        environment: "live" as const,
      }),
      onCopyToClipboardSuccess: ({ field }) => onCopy(field),
      onError: (error) => {
        console.error(error);
        if (settled) {
          onLateError(error);
          return;
        }
        settled = true;
        reject(error);
      },
      onReady: () => {
        ready = true;
        unmask();
        if (!settled) {
          settled = true;
          resolve();
        }
      },
      elements: {
        cardNumber: { selector: selectors.cardNumber, styles },
        cvv: { selector: selectors.cvv, styles },
        expirationDate: { selector: selectors.expirationDate, styles },
      },
    })
      .then(({ toggleCardNumberMask }) => {
        toggleMask = toggleCardNumberMask;
        unmask();
      })
      .catch((error) => {
        if (settled) return;
        settled = true;
        reject(error);
      });
  });
